import React from "react"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { HomepageCategoryItem } from "services/game/type"

interface HomepageRowProps {
  item: HomepageCategoryItem
  positionLabel: number
  onToggle: () => void
}

const titleFor = (item: HomepageCategoryItem): string => {
  const title = item.title
  if (title && typeof title === "object") {
    return title.en || title.ru || title.es || item.key
  }
  return item.key
}

export const HomepageRow = ({ item, positionLabel, onToggle }: HomepageRowProps) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1
  }

  const dragLabel = t("gameManagement.homepage.dragHandle", { defaultValue: "Drag to reorder" })

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={clsx(
        "flex items-center gap-3 px-4 py-3 rounded-lg border bg-white dark:bg-[#0C0C1A] text-[rgba(6,7,21,1)] dark:text-white transition-colors",
        "border-[rgba(132,_0,_168,_0.25)] dark:border-[rgba(255,255,255,0.1)]",
        isDragging && "bg-[rgba(132,_0,_168,_0.08)] dark:bg-[rgba(132,_0,_168,_0.15)]"
      )}
    >
      <span className="font-mono text-xs opacity-70 w-6 text-center">{positionLabel}</span>
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 opacity-60 hover:opacity-100"
        aria-label={dragLabel}
        title={dragLabel}
      >
        ⋮⋮
      </button>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{titleFor(item)}</div>
        <div className="text-xs opacity-60 font-mono">
          {item.key}
          <span className="ml-2 opacity-50">
            [{item.source_type.toLowerCase()}
            {!item.is_orderable && `, ${t("gameManagement.homepage.autoTag", { defaultValue: "auto" })}`}]
          </span>
        </div>
      </div>
      <label className="flex items-center gap-2 text-xs opacity-80 cursor-pointer">
        <input
          type="checkbox"
          className="toggle toggle-sm"
          checked={item.enabled}
          onChange={onToggle}
          aria-label={t("gameManagement.homepage.visibleAria", { defaultValue: "Visible on homepage" })}
        />
        <span className="select-none">{t("gameManagement.homepage.visible", { defaultValue: "visible" })}</span>
      </label>
    </li>
  )
}
